import { trackEvent } from '@fs/zion-analytics'
import { ControlHighlight } from '@fs/zion-icon'
import { Button, IconButton } from '@fs/zion-ui'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useZionFormContext } from '@fs/zion-form'
import { useSlsHighlightsContext } from '@fs/zion-sls'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import HighlightPanel from '../highlightPanel/HighlightPanel'

const highlightButtonCss = css`
  margin-top: -0.5rem;
  margin-inline-end: 0;
  margin-bottom: 0.5rem;
  margin-inline-start: 0.5rem;
`
// Utility function to safely get nested values from an object using a dot-separated path
const deepGet = (obj, path) => {
  return path?.match(/([^[\].]+)|\[(\d+)]/g)?.reduce((acc, key) => {
    const matchNumber = key.match(/^\[(\d+)]$/)
    if (matchNumber) {
      // If key is a number, treat as array index
      return acc?.[Number(matchNumber[1])]
    }
    return acc?.[key]
  }, obj)
}

const HighlightButton = ({
  field,
  editingSharedFieldHighlights,
  onClick = () => {},
  onSave = () => {},
  onCancel = () => {},
  disabled,
  buttonProps,
  iconOnly = false,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()
  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes(FIELDS.HIGHLIGHTS)
  const formContext = useZionFormContext()
  const { setActiveFieldInfo, getHighlights, activeFieldInfo } = useSlsHighlightsContext()
  const isEditing = useMemo(
    () =>
      getHighlights().mainEditHighlights?.some((highlight) => highlight.fieldId === field?.id) ||
      getHighlights().splitScreenEditHighlights?.some((highlight) => highlight.fieldId === field?.id),
    [getHighlights, field?.id]
  )
  const parsedField = field?.subElements?.[0] || field

  const getFieldValue = useCallback(() => {
    const { getValues } = formContext || {}
    const { id, name, value } = parsedField

    if (getValues) {
      const formValues = getValues()
      const valueFromFormContext = formValues[id] || formValues[name] || deepGet(formValues, name)

      if (
        valueFromFormContext?.originalText ||
        valueFromFormContext?.standard ||
        valueFromFormContext?.value || // values managed by useFieldArray may be in this format
        typeof valueFromFormContext === 'string' ||
        // The only field that sends an object in this style is the SEX_CODE field
        // if this changes, we need to change the code here and the code in EditHighlights.js
        // Added on Fulltext24 170
        (valueFromFormContext?.primaryText && parsedField.fieldType === 'SEX_CODE')
      ) {
        return valueFromFormContext
      }
    }

    return value || ''
  }, [parsedField, formContext])

  if (iconOnly) {
    return (
      <>
        <IconButton
          data-testid="edit-highlight-button"
          disabled={editingSharedFieldHighlights || isRestricted || disabled}
          Icon={ControlHighlight}
          title={
            isEditing
              ? t('edit-index.edit-highlight.button', 'Edit Highlight')
              : t('edit-index.add-highlight.button', 'Add Highlight')
          }
          linkName={isEditing ? 'Edit Highlight' : 'Add Highlight'}
          onClick={(event) => {
            trackEvent({
              event_name: 'click_action',
              link_name: `Highlight: Open`,
            })
            onClick(field, event)
            setActiveFieldInfo({ ...parsedField, value: getFieldValue() })
          }}
          {...buttonProps}
        />
        {activeFieldInfo?.id === field?.id && (
          <HighlightPanel splitScreenImageArk={splitScreenImageArk} onSave={onSave} onCancel={onCancel} />
        )}
      </>
    )
  }

  return (
    <>
      <div css={highlightButtonCss}>
        <Button
          data-testid="edit-highlight-button"
          disabled={editingSharedFieldHighlights || isRestricted || disabled}
          Icon={ControlHighlight}
          iconSize="lg"
          linkName={isEditing ? 'Edit Highlight' : 'Add Highlight'}
          onClick={(event) => {
            trackEvent({
              event_name: 'click_action',
              link_name: `Highlight: Open`,
            })
            onClick(field, event)
            setActiveFieldInfo({ ...parsedField, value: getFieldValue() })
          }}
          {...buttonProps}
        >
          {isEditing
            ? t('edit-index.edit-highlight.button', 'Edit Highlight')
            : t('edit-index.add-highlight.button', 'Add Highlight')}
        </Button>
      </div>
      {activeFieldInfo?.id === field?.id && (
        <HighlightPanel splitScreenImageArk={splitScreenImageArk} onSave={onSave} onCancel={onCancel} />
      )}
    </>
  )
}

export default HighlightButton
